import React, { useState, useEffect } from 'react';
import styles from './NotificationsSidebar.module.css';
import { X, ExternalLink, Sparkles, Briefcase, Building2 } from 'lucide-react';
import { apiService } from '../services/api';
import Tooltip from './Tooltip';

interface Insight {
  insightId: string;
  missingKeyword: string;
  questionToUser: string;
  context: string;
  jobTitle: string;
  companyName: string;
  jobUrl: string;
  matchScore: number;
  status: 'unread' | 'read' | 'dismissed' | 'applied';
  createdAt: string;
  candidateJobId: string;
}

interface NotificationsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  versionId: string;
  onAddressInsights: () => void;
}

export const NotificationsSidebar: React.FC<NotificationsSidebarProps> = ({
  isOpen,
  onClose,
  projectId,
  versionId,
  onAddressInsights
}) => {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && projectId) {
      loadInsights();
    }
  }, [isOpen, projectId]);

  const loadInsights = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.getInsights(projectId);
      setInsights(response.insights);
    } catch (err) {
      console.error('Error loading insights:', err);
      setError('Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  const handleJobUrlClick = (url: string) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      const hours = Math.floor(diffInHours);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    } else {
      const days = Math.floor(diffInHours / 24);
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`${styles.backdrop} ${isOpen ? styles.backdropOpen : ''}`}
        onClick={onClose}
      />
      
      {/* Sidebar */}
      <div className={`${styles.sidebar} ${isOpen ? styles.sidebarOpen : ''}`}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h2 className={styles.title}>
              <Sparkles className={styles.titleIcon} />
              Market Insights
            </h2>
            <p className={styles.subtitle}>
              Personalized recommendations based on job market analysis
            </p>
          </div>
          <button
            onClick={onClose}
            className={styles.closeButton}
            aria-label="Close notifications"
          >
            <X size={20} />
          </button>
        </div>

        <div className={styles.content}>
          {loading && (
            <div className={styles.loadingState}>
              <div className={styles.loadingSpinner} />
              <p>Loading insights...</p>
            </div>
          )}

          {error && (
            <div className={styles.errorState}>
              <p>{error}</p>
              <button onClick={loadInsights} className={styles.retryButton}>
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && insights.length === 0 && (
            <div className={styles.emptyState}>
              <Sparkles className={styles.emptyIcon} />
              <h3>No insights yet</h3>
              <p>As you explore job opportunities, we'll provide personalized insights to enhance your resume.</p>
            </div>
          )}

          {!loading && !error && insights.length > 0 && (
            <div className={styles.insightsList}>
              {insights.map((insight) => (
                <div key={insight.insightId} className={styles.insightCard}>
                  <div className={styles.insightHeader}>
                    <div className={styles.jobInfo}>
                      <div className={styles.jobTitle}>
                        <Briefcase className={styles.jobIcon} />
                        <span>{insight.jobTitle}</span>
                      </div>
                      <div className={styles.companyName}>
                        <Building2 className={styles.companyIcon} />
                        <span>{insight.companyName}</span>
                      </div>
                    </div>
                    <Tooltip content="View job posting">
                      <button
                        onClick={() => handleJobUrlClick(insight.jobUrl)}
                        className={styles.linkButton}
                        aria-label="View job posting"
                      >
                        <ExternalLink size={16} />
                      </button>
                    </Tooltip>
                  </div>

                  <div className={styles.insightContent}>
                    <div className={styles.missingKeyword}>
                      <span className={styles.keywordLabel}>Missing skill:</span>
                      <span className={styles.keywordValue}>{insight.missingKeyword}</span>
                    </div>
                    
                    <p className={styles.question}>{insight.questionToUser}</p>
                    
                    {insight.context && (
                      <p className={styles.context}>{insight.context}</p>
                    )}
                  </div>

                  <div className={styles.insightFooter}>
                    <span className={styles.matchScore}>
                      {insight.matchScore}% match
                    </span>
                    <span className={styles.timestamp}>
                      {formatDate(insight.createdAt)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {!loading && !error && insights.length > 0 && (
          <div className={styles.footer}>
            <button
              onClick={onAddressInsights}
              className={styles.addressButton}
            >
              <Sparkles size={18} />
              Address these insights with AI
            </button>
          </div>
        )}
      </div>
    </>
  );
};